import React, { useState, useEffect, useRef } from 'react';
import { AxiosAdminInstance } from '../utils';
import { toast } from 'react-toastify';
import Editor from '@monaco-editor/react';
import DataTable from 'react-data-table-component';

export const TemplateManager = () => {
    const [tree, setTree] = useState<any>({});
    const [selectedPath, setSelectedPath] = useState<string>(''); // Current selected folder path
    const [selectedFile, setSelectedFile] = useState<string>(''); // Current selected file path
    const [fileContent, setFileContent] = useState<string>('');
    const [newFile, setNewFile] = useState<string>('');
    const [selectedType, setSelectedType] = useState<string>('email'); // Default to email
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [loadingFile, setLoadingFile] = useState<boolean>(false);
    const [showAddFile, setShowAddFile] = useState<boolean>(false);

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editContent, setEditContent] = useState<string>('');
    const editorRef = useRef<any>(null);

    useEffect(() => {
        loadTemplates();
    }, []);

    // Clear editor content when folder selection changes
    useEffect(() => {
        if (selectedPath) {
            setSelectedFile('');
            setFileContent('');
            setIsEditing(false);
            setEditContent('');
        }
    }, [selectedPath]);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const { data } = await AxiosAdminInstance.get('/api/admin/listTemplates');
            const templates = data.templates; // array of 'type/file' strings
            const tree: any = {};
            templates.forEach((templatePath: string) => {
                const [type, fileName] = templatePath.split('/');
                if (!tree[type]) tree[type] = {};
                tree[type][fileName] = templatePath;
            });
            setTree(tree);
        } catch (error) {
            console.log(error);
            toast.error('Failed to load templates');
        }
        setLoading(false);
    };

    const loadFile = async (filePath: string) => {
        setLoadingFile(true);
        try {
            const fullPath = `templates/${filePath}`;
            const { data } = await AxiosAdminInstance.get('/api/admin/getTemplate', { params: { path: fullPath } });
            setFileContent(data.content);
            setSelectedFile(filePath);

            // Extract folder path from file path
            const lastSlash = filePath.lastIndexOf('/');
            const folderPath = lastSlash > 0 ? filePath.substring(0, lastSlash) : '';
            setSelectedPath(folderPath);
        } catch (error) {
            toast.error('Failed to load file');
        }
        setLoadingFile(false);
    };

    const saveFile = async () => {
        if (!selectedPath) return;
        try {
            const path = `templates/${selectedPath}`;
            await AxiosAdminInstance.post('/api/admin/saveTemplate', { path, content: fileContent });
            toast.success('File saved successfully');
        } catch (error) {
            toast.error('Failed to save file');
        }
    };

    const createFile = async () => {
        if (!newFile) {
            toast.error('Please enter a Template name');
            return;
        }

        // Trim whitespace
        const trimmedFileName = newFile.trim();
        if (!trimmedFileName) {
            toast.error('Template name cannot be empty or just spaces');
            return;
        }

        // Validate Template name: only lowercase alphanumeric and underscores
        if (!/^[a-z0-9_]+$/.test(trimmedFileName)) {
            toast.error('Invalid Template name. Only lowercase alphanumeric characters and underscores are allowed.');
            return;
        }

        setCreating(true);
        try {
            const fullPath = `templates/${selectedType}/${trimmedFileName}`;
            await AxiosAdminInstance.post('/api/admin/createTemplate', { path: fullPath, content: '{}' });
            setNewFile('');
            setShowAddFile(false);
            loadTemplates();
            toast.success('File created successfully');

            // Automatically load the newly created file in the editor
            loadFile(`${selectedType}/${trimmedFileName}`);
        } catch (error) {
            toast.error('Failed to create file');
        }
        setCreating(false);
    };

    const getTableData = () => {
        const data: any[] = [];
        Object.keys(tree).forEach(type => {
            const typeNode = tree[type];
            Object.keys(typeNode).forEach(fileKey => {
                if (typeof typeNode[fileKey] === 'string') {
                    const filePath = typeNode[fileKey];
                    const fileName = fileKey;
                    data.push({
                        type,
                        fileName,
                        filePath,
                        isSelected: selectedFile === filePath
                    });
                }
            });
        });
        return data;
    };

    const tableColumns = [
        { name: 'Type', selector: (row: any) => row.type, sortable: true },
        { 
            name: 'File', 
            cell: (row: any) => (
                <span
                    onClick={() => {
                        setSelectedFile(row.filePath);
                        const lastSlash = row.filePath.lastIndexOf('/');
                        const folderPath = lastSlash > 0 ? row.filePath.substring(0, lastSlash) : '';
                        setSelectedPath(folderPath);
                        setLoadingFile(true);
                        loadFile(row.filePath);
                    }}
                    style={{
                        fontWeight: row.isSelected ? 'bold' : 'normal',
                        cursor: 'pointer',
                        color: row.isSelected ? '#007bff' : 'inherit'
                    }}
                >
                    📄 {row.fileName}
                </span>
            ),
            sortable: true
        }
    ];

    return (
        <section className="register-sec sec-padding-admin" data-scroll-index="8">
            <div className="container">
                <div className="sec-title">
                    <span className="icon-inner wow flipInY" data-wow-duration="300ms"><span className="fa-stack"><i className="fa rhex fa-stack-2x"></i><i className="fa fa-user fa-stack-1x"></i></span></span>
                    <h3 className="wow fadeInRight" data-wow-duration="500ms">Template Manager</h3>
                </div>
                <div className="register-sec-details">
                    <form>
                        {/* Templates Table */}
                        <fieldset className="form-group p-3">
                            <legend className="w-auto px-2 subheading">Templates</legend>
                            <div className="row pt-2">
                                <div className="col-lg-12 col-md-12 col-sm-12">
                                    <DataTable
                                        columns={tableColumns}
                                        data={getTableData()}
                                        progressPending={loading}
                                        customStyles={{
                                            cells: {
                                                style: {
                                                    wordWrap: 'break-word',
                                                    whiteSpace: 'normal',
                                                    padding: '8px'
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Add File */}
                        <fieldset className="form-group p-3">
                            <legend className="w-auto px-2 subheading">Add New Template</legend>
                            <div className="row pt-2">
                                <div className="col-lg-12 col-md-12 col-sm-12">
                                    <div className="row g-2">
                                        <div className="col-md-3">
                                            <select className="form-control" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                                                <option value="email">Email</option>
                                                <option value="pdf">PDF</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Template Name"
                                                value={newFile}
                                                onChange={(e) => setNewFile(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <button className="btn btn-primary" onClick={(e) => {
                                                e.preventDefault();
                                                createFile();
                                            }} disabled={creating || !newFile.trim()}>
                                                Add File {creating && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        {/* Current selection info */}
                        {selectedPath && (
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Selected Template</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <div style={{ padding: '10px', backgroundColor: '#e8f4f8', borderRadius: '5px' }}>
                                            <strong>Type:</strong> {selectedPath}
                                            {selectedFile && (
                                                <div style={{ marginTop: '5px' }}>
                                                    <strong>File:</strong> {selectedFile.split('/').pop()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* File content display and editing */}
                        {selectedFile && fileContent && (
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Template Content</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <div style={{ marginBottom: '10px' }}>
                                            <div style={{ height: '400px', border: '1px solid #ddd', borderRadius: '5px', position: 'relative' }}>
                                                <Editor
                                                    height="100%"
                                                    defaultLanguage="json"
                                                    value={isEditing ? editContent : fileContent}
                                                    onChange={(value) => isEditing ? setEditContent(value || '') : undefined}
                                                    onMount={(editor, monaco) => {
                                                        editorRef.current = editor;
                                                        if (isEditing) {
                                                            setTimeout(() => {
                                                                editor.getAction('editor.action.formatDocument')?.run();
                                                            }, 100);
                                                        }
                                                    }}
                                                    theme="vs-light"
                                                    options={{
                                                        minimap: { enabled: false },
                                                        fontSize: 14,
                                                        lineNumbers: 'on',
                                                        roundedSelection: false,
                                                        scrollBeyondLastLine: false,
                                                        automaticLayout: true,
                                                        wordWrap: 'on',
                                                        formatOnPaste: true,
                                                        formatOnType: true,
                                                        tabSize: 2,
                                                        insertSpaces: true,
                                                        readOnly: !isEditing || loadingFile
                                                    }}
                                                />
                                                {loadingFile && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                        zIndex: 10
                                                    }}>
                                                        <i className="fa fa-circle-o-notch fa-spin fa-2x"></i>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            {!isEditing ? (
                                                <button className="btn btn-warning" onClick={(e) => {
                                                    e.preventDefault();
                                                    setIsEditing(true);
                                                    setEditContent(fileContent);
                                                }}>Edit Content</button>
                                            ) : (
                                                <>
                                                    <button className="btn btn-success" onClick={async (e) => {
                                                        e.preventDefault();
                                                        setSaving(true);
                                                        try {
                                                            const path = `templates/${selectedFile}`;
                                                            await AxiosAdminInstance.post('/api/admin/saveTemplate', { path, content: editContent });
                                                            setFileContent(editContent);
                                                            setIsEditing(false);
                                                            toast.success('File saved successfully');
                                                        } catch (error) {
                                                            toast.error('Failed to save file');
                                                        }
                                                        setSaving(false);
                                                    }} disabled={saving}>
                                                        {saving ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                    <button className="btn btn-secondary ms-2" onClick={(e) => {
                                                        e.preventDefault();
                                                        setIsEditing(false);
                                                        setEditContent('');
                                                    }}>Cancel</button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {!selectedPath && (
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Instructions</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <p style={{ textAlign: 'center', color: '#666' }}>
                                            Select a file from the table above to view or edit its contents.
                                        </p>
                                    </div>
                                </div>
                            </fieldset>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};
